/**
 * Landing a typed date on a day the market was open.
 *
 * The date box is a **chart position**, not a calendar. On a Sunday there is no
 * bar for today, so offering today means offering a date the chart cannot
 * honour: press Enter and it does nothing, or jumps somewhere unexplained. It
 * opens on the last trading day instead, which is also what the chart's right
 * edge is already showing — so the box agrees with the chart rather than
 * contradicting it.
 *
 * ## Why the picker cannot simply forbid a holiday
 *
 * `<input type="date">` supports `min` and `max` and **nothing else**. There is
 * no way to grey out individual dates in a native picker; the only route to that
 * is a hand-built calendar popup, which is a surprising amount of UI for a
 * control most people type into.
 *
 * So a closed day can still be picked, and is **snapped backwards with the
 * reason shown** — "4 July was a holiday, showing 2 July" — rather than silently
 * moving or silently doing nothing. Snapping backwards, never forwards: the bar
 * before a closure exists, the one after it may not have happened yet.
 *
 * `min`/`max` are still set, from the loaded bar range, because those the native
 * picker *does* honour — which turns "there is no data that far back" from a
 * snap into something the calendar simply will not offer.
 */

/** `yyyy-mm-dd`, the one format `<input type="date">` reads and writes. */
export type IsoDate = string;

const DAY_MS = 86_400_000;

/**
 * Parsed as UTC, deliberately.
 *
 * `new Date("2026-07-04")` is already UTC midnight, but `new Date(2026, 6, 4)`
 * is local — and mixing them puts a date on the wrong side of its own bar for
 * anyone west of Greenwich. Bars carry UTC-midnight dates, so everything here
 * stays in that frame and never converts.
 */
function toUtc(iso: IsoDate): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(ms) ? null : ms;
}

function toIso(ms: number): IsoDate {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Saturday or Sunday, in UTC. */
function isWeekend(ms: number): boolean {
  const day = new Date(ms).getUTCDay();
  return day === 0 || day === 6;
}

export interface SnapResult {
  /** The date to use. Unchanged when the asked-for day was already open. */
  date: IsoDate;
  /** Why it moved, for showing to the user. Absent when it did not. */
  reason?: string;
}

/**
 * The nearest trading day at or before `iso`.
 *
 * Bounded at ten days rather than looping: a calendar that somehow marked a
 * fortnight closed would otherwise search forever, and the longest real closure
 * on record is four days. Past the bound it returns what it was given — visibly
 * wrong beats a hang.
 */
export function snapToTradingDay(
  iso: IsoDate,
  holidays: readonly IsoDate[],
): SnapResult {
  const start = toUtc(iso);
  if (start === null) return { date: iso };

  const closed = new Set(holidays);
  let ms = start;
  for (let i = 0; i < 10; i++) {
    const day = toIso(ms);
    if (!isWeekend(ms) && !closed.has(day)) {
      if (i === 0) return { date: day };
      return {
        date: day,
        reason: closed.has(iso)
          ? `${iso} was a market holiday — showing ${day}`
          : `${iso} was a weekend — showing ${day}`,
      };
    }
    ms -= DAY_MS;
  }
  return { date: iso };
}

/**
 * What the box should show when nothing has been typed.
 *
 * The server's `lastTradingDay` when we have one, because it walked the real
 * calendar. Falling back to snapping locally covers the offline case, where the
 * holiday list is empty and the answer degrades to "the last weekday" — right
 * about 251 days in 261, and wrong only into a closure.
 */
export function defaultDate(
  calendar: { lastTradingDay?: IsoDate; today: IsoDate; holidays: readonly IsoDate[] },
): IsoDate {
  return calendar.lastTradingDay || snapToTradingDay(calendar.today, calendar.holidays).date;
}

/**
 * `min`/`max` for the picker, from the bars actually loaded.
 *
 * The native picker honours these, so a date with no data behind it is one the
 * calendar will not offer — better than accepting it and then explaining. Absent
 * when there are no bars yet, since a range of nothing would disable the control
 * during the first load.
 *
 * `max` is never before `today`: the picker greys its own **Today** button when
 * today is past `max`, and on a weekend or a holiday — or before today's bar
 * lands — the last bar is always before today (owner, 2026-11-01). A closed
 * today is snapped back to the last session with the reason shown, like any
 * other closed day.
 */
export function pickerRange(
  barDates: readonly IsoDate[],
  latest?: IsoDate,
  today?: IsoDate,
): { min?: IsoDate; max?: IsoDate } {
  const atLeastToday = (max: IsoDate | undefined) => (today && (!max || today > max) ? today : max);
  if (barDates.length === 0) {
    // No bars yet, but the future is still bounded: there is no chart past
    // today, so the calendar should not offer one. A `min` needs the loaded
    // range and is left open rather than guessed at — an invented floor would
    // hide history that is actually there.
    const max = atLeastToday(latest);
    return max ? { max } : {};
  }
  return { min: barDates[0], max: atLeastToday(barDates[barDates.length - 1]) };
}
